import {
  LocationACL,
  Profile,
  ResourceACL,
  ResourceProfile,
  Theme,
} from '@raydiant/api-client-js';
import { ThemesById } from '../../selectors/v2/themes';

export const getSharedThemesById = (
  themesById: ThemesById,
  currentUser: Profile,
): ThemesById => {
  return Object.entries(themesById).reduce((acc, [key, theme]) => {
    if (
      theme &&
      theme.resource &&
      theme.resource.r &&
      theme.resource.r.resourceACLs &&
      theme.resource.r.resourceACLs.some(
        (acl) =>
          (acl.grantProfileId === currentUser.id ||
            acl.grantDomainId === currentUser.domainId) &&
          acl.readGrant,
      )
    ) {
      acc[key] = theme;
    }
    return acc;
  }, {} as Record<string, Theme>);
};

export const getThemesByProfileId = (themesById: ThemesById) => {
  if (!themesById) return [];

  const result: { profile: ResourceProfile; themes: Theme[] }[] = [];

  Object.values(themesById).forEach((theme) => {
    if (!theme || !theme.resource || !theme.resource.profile) return;

    const profileId = theme.resource.profile.id;
    const existingProfile = result.find(
      (entry) => entry.profile.id === profileId,
    );

    if (existingProfile) {
      existingProfile.themes.push(theme);
    } else {
      result.push({
        profile: theme.resource.profile,
        themes: [theme],
      });
    }
  });

  return result;
};

export const mergeThemesByProfile = (
  domainThemesByProfile: { profile: ResourceProfile; themes: Theme[] }[],
  sharedThemesByProfile: { profile: ResourceProfile; themes: Theme[] }[],
) => {
  const result = [...domainThemesByProfile];

  sharedThemesByProfile.forEach((sharedEntry) => {
    const existingProfileEntry = result.find(
      (entry) => entry.profile.id === sharedEntry.profile.id,
    );

    if (existingProfileEntry) {
      sharedEntry.themes.forEach((theme) => {
        const alreadyExists = existingProfileEntry.themes.some(
          (t) => t.id === theme.id,
        );
        if (!alreadyExists) {
          existingProfileEntry.themes.push(theme);
        }
      });
    } else {
      result.push({
        profile: sharedEntry.profile,
        themes: [...sharedEntry.themes],
      });
    }
  });

  return result
    .map((entry) => ({
      profile: {
        ...entry.profile,
      },
      themes: [...entry.themes].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.profile.name.localeCompare(b.profile.name));
};

export const getLocationACLsToThemeResourceACLs = (
  theme: Theme,
  locationACLs: LocationACL[],
  currentUser: Profile,
) => {
  const existingResourceACLs = theme.resource?.r?.resourceACLs
    ? [...theme.resource.r.resourceACLs]
    : [];

  const resourceACLs: ResourceACL[] = locationACLs.map((locationACL) => {
    const existingResourceACL = existingResourceACLs.find(
      (resourceACL) => resourceACL.id === locationACL.id,
    );

    const {
      id,
      grantDomainId,
      grantScope,
      readGrant,
      updateGrant,
      deleteGrant,
      shareGrant,
      customAccessGrants,
      fullGrant,
    } = locationACL;

    if (existingResourceACL) {
      return {
        ...existingResourceACL,
        readGrant,
        updateGrant,
        deleteGrant,
        shareGrant,
        customAccessGrants,
        fullGrant,
      };
    } else {
      return {
        id,
        resourceId: theme.resource?.id,
        profileId: theme.resource?.profile?.id,
        grantProfileId: locationACL.grantProfile?.id || '',
        grantDomainId: grantDomainId || '',
        grantScope,
        readGrant,
        updateGrant,
        deleteGrant,
        shareGrant,
        fullGrant,
        customAccessGrants,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        r: {
          profile: {
            id: currentUser.id,
            email: currentUser.email,
            name: currentUser.name,
            thumbnailUrl: currentUser.thumbnailUrl || '',
            domainId: currentUser.domainId,
            domainRole: currentUser.domainRole,
          },
          grantProfile: {
            id: locationACL.grantProfile?.id,
            email: locationACL.grantProfile?.email,
            name: locationACL.grantProfile?.name,
            thumbnailUrl: locationACL.grantProfile?.thumbnailUrl || '',
            domainId: locationACL.grantProfile?.domainId,
            domainRole: locationACL.grantProfile?.domainRole,
          },
        },
      };
    }
  });

  return resourceACLs;
};
